import React, { useCallback, useMemo, useRef } from 'react';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider as BaseApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';

// JME
// import { useAuthHook } from '#utils/useAuthHook';
// JME1
import { useAuth } from '#core/Auth';
import { useEnvironment } from '#core/Environment';
import { useInstitution } from '#core/Institution';

/*
  NOTE: This provider is used twice in app.tsx, once near the top of tree to get the list of
  institutions available to user and again further down the tree once the Institution context
  has been defined

  Be careful when adding something like a websocket connection to this provider or we may
  end up with double the amount of connections
*/
const ApolloProvider: React.FC<{}> = ({ children }) => {
  const { apiEndpoint, graURL } = useEnvironment();
  const { apiHeaders: institutionApiHeaders } = useInstitution();
  const { apiHeaders: authApiHeaders, permissionToken } = useAuth(); // JME2

  const authHeaders = useRef({});

  authHeaders.current = {
    ...authApiHeaders,
  };

  const httpLink = from([
    new HttpLink({
      uri: `//${apiEndpoint}/graphql`,
    }),
  ]);

  // TODO: update to real link when generic-ranking-api is ready
  const genericApiLink = from([
    new HttpLink({
      uri: graURL,
    }),
  ]);

  const linkCallback = useCallback(
    (_, { headers }) => ({
      headers: {
        ...headers,
        ...authHeaders.current,
        ...institutionApiHeaders,
      },
    }),
    [authHeaders, institutionApiHeaders],
  );

  const graLinkCallback = useCallback(
    (_, { headers }) => ({
      headers: {
        ...headers,
        ...authHeaders.current,
        ...institutionApiHeaders,
        'permissions-token': permissionToken,
      },
    }),
    [authHeaders, institutionApiHeaders, permissionToken],
  );

  return useMemo(() => {
    const authLink = setContext(linkCallback);
    const graAuthLink = setContext(graLinkCallback);
    const apolloClient = new ApolloClient({
      link: ApolloLink.split(
        (operation) =>
          operation.getContext().clientName === 'generic-ranking-api' ||
          [
            'NewReleasePillars',
            'NewReleaseCrossPillars',
            'GetNewWURMetricsOveralScores',
            'GetNewWURMetricsComparison',
            'GetBibliometricAnalysisPageDetails',
            'GetBibliometricScores',
            'GetOverviewSummary',
            'GetOverviewPerformance',
            'GetNewLevelDefinitions',
            'GetChangeOverYear',
            'GetOverviewPillarScores',
            'GetBenchmarkSummaryBenchmarkInstitutions',
            'GetBenchmarkSummaryBenchmarkStatistics',
            'GetBenchmarkSummaryPeersCards',
            'NewReleaseSubjects',
            'NewReleaseIndividualSubject',
            'GetPeerGroupAnalysisTable',
            'GetPeerGroupAnalysisCards',
            'GetYearOnYearChangesInPerformance',
            'GetYearOnYearContributingFactors',
            'GetYearOnYearPerformanceDistribution',
            'GetGRADetailedSubjectsPageDetails',
            'GetOverallDetailedSubjectScores',
            'GetDetailedSubjectMetricScores',
            'WurDashboardPageQuery',
            'GetReleaseDatesQuery',
            'AvailableYears',
            'GetSubjectAndClusterNames',
            'GetRankData',
            'GetAllRankedInstitutions',
            'GetOverallLevel1ResultsForInstitutions',
          ].includes(operation.operationName),
        graAuthLink.concat(genericApiLink), // <= apollo will send to this if clientName is "generic-ranking-api"
        authLink.concat(httpLink), // <= otherwise will send to this
      ),
      cache: new InMemoryCache(),
    });
    return (
      <BaseApolloProvider client={apolloClient}>{children}</BaseApolloProvider>
    );
  }, [linkCallback]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default ApolloProvider;
